import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'

import { AdditionalApplicationDataProps, UseAppFormLogicProps, UseAppFormLogicReturnProps } from '../AppFormProps'
import useFormHandlers from './useFormHandlers'
import useInputFactoryLogic from 'components/Form/Fields/InputFactory/hooks/useInputFactoryLogic'
import { AppLinks } from 'enviroment/AppLinks'
import { IAppConsentsModel } from 'service/SettingsService/SettingsServiceModels'
import { FormSubmitionService } from 'service/FormSubmitionService/FormSubmitionService'
import { ILoanAmountLimitationsModel, IVideoVerificationAdditionalData } from 'service/FormSubmitionService/FormSubmitionModels'
import { FormStateModel } from 'store/FormSlice/FormSliceModel'
import { changeWaitingScreenText, setAlphaId } from 'store/ApplicationSlice/ApplicationSlice'
import { IFormDefinition } from 'models/Form/FormModels'
import { IFields, TFieldsObjectNames, TFieldsType } from 'models/Form/FieldsModels'
import useTextContents from 'common/hooks/useTextContents'
import apphistory from 'common/AppHistory'


const useAppFormLogic = (props: UseAppFormLogicProps): UseAppFormLogicReturnProps => {
   const navigate = useNavigate()
   const dispatch = useDispatch()
   const appFormTextContents = useTextContents()

   const { handleValidateForm, checkIsRequiredConsentsMarked } = useFormHandlers()
   const { fieldErrorClassName } = useInputFactoryLogic()
   const [formHasError, setFormHasError] = useState<boolean | undefined>(undefined)
   const hasPageBeenRendered = useRef(false)


   useEffect(() => { // Scroll do pierwszej kontrolki z błędną walidacją
      const scrollToErrorInput: Element = document.getElementsByClassName(fieldErrorClassName)[0]

      if(scrollToErrorInput !== undefined && formHasError) {
         scrollToErrorInput.scrollIntoView({ behavior:"smooth", block: "center" })
      }

      setFormHasError(undefined)

   }, [formHasError])


   /**
    * Funkcja zwracająca formularz aplikacji, z różną ilością pól w zależności od warunku.
    * 
    * ZAŁOŻENIE BIZNESOWE: 
    * Gdy wnioskowana kwota jest niższa od zadanego progu z Mongo, zbieramy ograniczoną liczbę pól
    * Gdy jest wyższa zbieramy rozszerzoną ilość informacji
   */
   const setCurrentFormFields = (currentForm: IFormDefinition, limitedFieldsNames: TFieldsObjectNames[], sourceFields: IFields, additionalApplicationData: AdditionalApplicationDataProps ): IFormDefinition => {
      const newCurrentForm =  currentForm

      if(!hasPageBeenRendered.current) {
         if(additionalApplicationData.amountToPay < additionalApplicationData.minimalLoanAmountToDisplayAdditionalFields) {
            let newFields: Partial<IFields> = {}

            for (const fieldName of limitedFieldsNames) {
               newFields = {
                  ...newFields,
                  [fieldName]: (sourceFields as Record<TFieldsObjectNames, TFieldsType>)[fieldName]
               }
            }

            newCurrentForm.fields = newFields as IFields

            return newCurrentForm
         }
      }

      hasPageBeenRendered.current = true // Pierwszy render strony

      return newCurrentForm
   }

   /**
    * Funkcja do obsługi zdarzenia kliknięcia w przycisk Submit na PIERWSZEJ stronie Formularza (Formularz z danymi podstawowymi) 
   */
   const handleBasicInfoFormSubmitButtonClick = (e: React.SyntheticEvent, appConsents: IAppConsentsModel[]) => {
      e.preventDefault()
      const isFormValid = handleValidateForm(props.form)


      if(isFormValid) {
         setFormHasError(false)
         const hasConsentsError = checkIsRequiredConsentsMarked(appConsents) // Sprawdzamy czy wszystkie zgody wymagane są zaznaczone

         if(props.form.nextStepUrlPath !== undefined && isFormValid && !hasConsentsError) { //Tylko jeśli formularz przeszedł walidację i wszystkie wymagane zgody są zaznaczone można przejść dalej
            navigate(props.form.nextStepUrlPath)
         }
      }
      else {
         setFormHasError(true)
      }
   }

   /**
    * Funkcja do obsługi zdarzenia kliknięcia w przycisk Submit na DRUGIEJ stronie Formularza (Formularz z danymi adresowymi) 
   */
   const handleAddressInfoFormSubmitButtonClick = React.useCallback((e: React.SyntheticEvent) => {
      e.preventDefault()
      const isFormValid = handleValidateForm(props.form)
      

      if(isFormValid) {
         setFormHasError(false)
         if(props.form.nextStepUrlPath !== undefined && isFormValid) {
            navigate(props.form.nextStepUrlPath)
         }
      }
      else {
         setFormHasError(true)
      }
   }, [])


   /**
    * Funkcja do obsługi zdarzenia kliknięcia w przycisk Submit na TRZECIEJ stronie Formularza (Formularz z danymi rozszerzonymi) 
   */
   const handleExtendedInfoFormSubmitButtonClick =  React.useCallback(async(
      e: React.SyntheticEvent,
      formState: FormStateModel,
      appConsents: IAppConsentsModel[],
      additionalApplicationData: AdditionalApplicationDataProps,
   ) => {
      e.preventDefault()
      const isFormValid = handleValidateForm(props.form)
     

      if(isFormValid) {
         setFormHasError(false)
         dispatch(changeWaitingScreenText(appFormTextContents('loaders.default'))) // Zmieniamy tekst na ekranie ładowania na zgodny z projektem
         navigate(AppLinks.waiting)

         const formSubmitionService = new FormSubmitionService()
         
         const loanLimitations: ILoanAmountLimitationsModel = {
            minimalLoanAmountToDisplayAdditionalFields: additionalApplicationData.minimalLoanAmountToDisplayAdditionalFields,
            amountToPay: additionalApplicationData.amountToPay
         }
   
         try {
            const formSubmitionData = formSubmitionService.mapFieldsToSubmitionModel(loanLimitations, formState, appConsents, additionalApplicationData.billingCompany)
            const submitResponse = await formSubmitionService.submitBasic(formSubmitionData)
            dispatch(setAlphaId(submitResponse.alphaId))
         }
         catch (e) {
            navigate(AppLinks.error)
         }
      }
      else {
         setFormHasError(true)
      }
   }, [])


   /**
    * Funkcja do obsługi zdarzenia kliknięcia w przycisk Submit na TRZECIEJ stronie Formularza (Formularz z danymi rozszerzonymi) 
   */
      const handleMObywatelExtendedInfoFormSubmitButtonClick =  React.useCallback(async(
         e: React.SyntheticEvent,
         formState: FormStateModel,
         appConsents: IAppConsentsModel[],
         additionalApplicationData: AdditionalApplicationDataProps,
      ) => {
         e.preventDefault()
         const isFormValid = handleValidateForm(props.form)
         const hasConsentsError = checkIsRequiredConsentsMarked(appConsents) // Sprawdzamy czy wszystkie zgody wymagane są zaznaczone
           
         if(isFormValid && !hasConsentsError) {
            setFormHasError(false)
            dispatch(changeWaitingScreenText(appFormTextContents('loaders.default'))) // Zmieniamy tekst na ekranie ładowania na zgodny z projektem
            navigate(AppLinks.waiting)
   
            const formSubmitionService = new FormSubmitionService()
      
            try {
               const formSubmitionData = formSubmitionService.mapFieldsToMObywatelSubmitionModel(formState, appConsents, additionalApplicationData)
               const submitResponse = await formSubmitionService.submitBasicMObywatel(formSubmitionData)
               dispatch(setAlphaId(submitResponse.alphaId))
            }
            catch (e) {
               navigate(AppLinks.error)
            }
         }
         else {
            setFormHasError(true)
         }
      }, [])


      /**
         * Funkcja do obsługi zdarzenia kliknięcia w przycisk Submit na PIERWSZEJ stronie Formularza w ścieżce WIDEO WERYFIKACJI (dane podstawowe) 
      */
      const handleVideoVerificationBasicInfoFormSubmitButtonClick =  React.useCallback(async(e: React.SyntheticEvent, appConsents: IAppConsentsModel[]) => {
         e.preventDefault()
         const isFormValid = handleValidateForm(props.form)
         const hasConsentsError = checkIsRequiredConsentsMarked(appConsents) // Sprawdzamy czy wszystkie zgody wymagane są zaznaczone
           
         if(isFormValid && !hasConsentsError) {
            setFormHasError(false)
            navigate(AppLinks.videoVerificationAddressInfoForm)
         }
         else {
            setFormHasError(true)
         }
      }, [])


      /**
         * Funkcja do obsługi zdarzenia kliknięcia w przycisk Submit na stronie formularza zbierającego dane dowodowe dla brokera TRASTI
      */
      const handleCollectPersonalIdFormSubmitButtonClick = (e: React.SyntheticEvent) => {
         e.preventDefault()
         const isFormValid = handleValidateForm(props.form)


         if(isFormValid) {
            apphistory.push(AppLinks.bmverify)
         }
         else {
            setFormHasError(true)
         }
      }

      /**
         * Funkcja do obsługi zdarzenia kliknięcia w przycisk Submit na DRUGIEJ stronie Formularza w ścieżce WIDEO WERYFIKACJI (dane adresowe) 
      */
      const handleVideoVerificationAddressInfoFormSubmitButtonClick =  React.useCallback(async( 
         e: React.SyntheticEvent, 
         formState: FormStateModel, 
         appConsents: IAppConsentsModel[], 
         additionalVideoVerificationData: IVideoVerificationAdditionalData
      ) => {
         e.preventDefault()
         const isFormValid = handleValidateForm(props.form)
         const hasConsentsError = checkIsRequiredConsentsMarked(appConsents) // Sprawdzamy czy wszystkie zgody wymagane są zaznaczone
           
         if(isFormValid && !hasConsentsError) {
            setFormHasError(false)
            dispatch(changeWaitingScreenText(appFormTextContents('loaders.default'))) // Zmieniamy tekst na ekranie ładowania na zgodny z projektem
            navigate(AppLinks.waiting)
   
            const formSubmitionService = new FormSubmitionService()
      
            try {
               const formSubmitionData = formSubmitionService.mapFieldsToVideoVerificationModel(formState, appConsents, additionalVideoVerificationData)
               const submitResponse = await formSubmitionService.submitBasicVideoVerification(formSubmitionData)
               dispatch(setAlphaId(submitResponse.alphaId))
            }
            catch (e) {
               navigate(AppLinks.error)
            }
         }
         else {
            setFormHasError(true)
         }
      }, [])

   return {
      handleBasicInfoFormSubmitButtonClick,
      handleAddressInfoFormSubmitButtonClick,
      handleExtendedInfoFormSubmitButtonClick,
      handleMObywatelExtendedInfoFormSubmitButtonClick,
      handleVideoVerificationBasicInfoFormSubmitButtonClick,
      handleVideoVerificationAddressInfoFormSubmitButtonClick,
      handleCollectPersonalIdFormSubmitButtonClick,
      setCurrentFormFields,
   }
}

export default useAppFormLogic